import * as React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArchiveRestore,
  ArrowLeft,
  Layers,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldQuestion,
  Trash2,
  X,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError, userHasPermission, type Section } from "@eduviaos/shared";
import { SectionEditorSheet } from "@/components/SectionEditorSheet";
import { useArchiveSection, useSections, useUnarchiveSection } from "@/hooks/useAcademicCatalogue";
import { useBranches } from "@/hooks/useBranches";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase H4 FE-1 — IA Catalogue → Sections.
 *
 * Branch + grade filter live above the table because sections are
 * scoped to `(branch, grade, year)`. Tenants typically have <100
 * sections live so we render the full filtered list unpaged with the
 * footer scaffolding ready for the day a multi-branch chain crosses
 * 200+.
 *
 * Iron Rule #16: sticky header in scroll region, action column last
 * with `…` dropdown trigger, no zebra stripes, `<StatusPill>` /
 * `<Badge>` used for archived state.
 */

const QUERY_KEY = "sections-catalogue";
const ALL_BRANCHES_VALUE = "__all_branches__";

export function SectionsCataloguePage() {
  const [branchFilter, setBranchFilter] = React.useState<string | null>(null);
  const [qInput, setQInput] = React.useState("");
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingSection, setEditingSection] = React.useState<Section | null>(null);
  const [pendingArchive, setPendingArchive] = React.useState<Section | null>(null);
  const [archiveError, setArchiveError] = React.useState<string | null>(null);

  const branchesQuery = useBranches({ status: "active" });
  // Memoise so the downstream useEffect (auto-pick flagship) and the
  // useMemo (`branchLookup`) don't trip the exhaustive-deps lint on a
  // freshly-rebuilt `branches` reference every render.
  const branches = React.useMemo(
    () => branchesQuery.data?.items ?? [],
    [branchesQuery.data?.items],
  );

  // Auto-pick the flagship (or first) branch once branches load so the
  // table isn't empty on first paint. The user can switch to "All" via
  // the dropdown if they want a global view.
  React.useEffect(() => {
    if (branchFilter !== null) return;
    if (branches.length === 0) return;
    const flagship = branches.find((branch) => branch.is_flagship) ?? branches[0];
    if (flagship) setBranchFilter(flagship.id);
  }, [branchFilter, branches]);

  const { data, isLoading, isFetching, refetch, isError, error } = useSections({
    branchId: branchFilter,
    includeArchived,
  });
  const archiveMutation = useArchiveSection();
  const unarchiveMutation = useUnarchiveSection();

  const { user } = useAuth();
  const canWrite = React.useMemo(
    () =>
      user
        ? userHasPermission(
            user.role,
            user.permission_overrides ?? [],
            "manage_settings",
            user.permission_mode ?? "additive",
          )
        : false,
    [user],
  );

  const items = React.useMemo<Section[]>(() => {
    const all = data?.items ?? [];
    const trimmed = qInput.trim().toLowerCase();
    if (!trimmed) return all;
    return all.filter(
      (section) =>
        section.name.toLowerCase().includes(trimmed) ||
        section.grade_ref.toLowerCase().includes(trimmed) ||
        section.academic_year_id.toLowerCase().includes(trimmed) ||
        (section.default_room_id ?? "").toLowerCase().includes(trimmed),
    );
  }, [data?.items, qInput]);

  const total = data?.items.length ?? 0;

  const branchLookup = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const branch of branches) {
      map.set(branch.id, branch.name);
    }
    return map;
  }, [branches]);

  const openCreate = () => {
    setEditingSection(null);
    setEditorOpen(true);
  };

  const openEdit = (section: Section) => {
    setEditingSection(section);
    setEditorOpen(true);
  };

  const handleSaved = (_saved: Section) => {
    // SectionEditorSheet fires its own toast.success; we just clear
    // local editing state so the next "New section" click reopens
    // blank.
    setEditingSection(null);
  };

  const handleConfirmArchive = async () => {
    if (!pendingArchive) return;
    setArchiveError(null);
    try {
      await archiveMutation.mutateAsync(pendingArchive.id);
      toast.success(`Archived ${pendingArchive.grade_ref}-${pendingArchive.name}.`);
      setPendingArchive(null);
    } catch (raw) {
      if (raw instanceof ApiError && raw.message) {
        setArchiveError(raw.message);
      } else if (raw instanceof Error && raw.message) {
        setArchiveError(raw.message);
      } else {
        setArchiveError("Could not archive — please try again.");
      }
    }
  };

  const handleUnarchive = async (section: Section) => {
    try {
      await unarchiveMutation.mutateAsync(section.id);
      toast.success(`Restored ${section.grade_ref}-${section.name}.`);
    } catch (raw) {
      const message =
        raw instanceof Error && raw.message ? raw.message : "Could not restore — please try again.";
      toast.error(message);
    }
  };

  const branchSelectValue = branchFilter ?? ALL_BRANCHES_VALUE;

  const handleBranchChange = (next: string) => {
    setBranchFilter(next === ALL_BRANCHES_VALUE ? null : next);
  };

  const noBranchesYet = !branchesQuery.isLoading && branches.length === 0;

  const defaultBranchId = branchFilter ?? branches[0]?.id ?? null;

  return (
    <div className="mx-auto max-w-6xl py-8" data-testid={QUERY_KEY}>
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/catalogue">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to Catalogue
        </Link>
      </Button>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Sections</PageHeaderHeading>
            <PageHeaderDescription>
              Per-branch class sections. The timetable solver, attendance roll-call, and eligibility
              matrix all read this list — sections without a class teacher show up in the readiness
              panel as a missing prerequisite.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
              aria-label="Refresh sections"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            {canWrite ? (
              <Button
                size="sm"
                onClick={openCreate}
                disabled={noBranchesYet}
                data-testid="sections-create"
              >
                <Plus aria-hidden="true" className="size-4" />
                New section
              </Button>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {!canWrite && user ? (
        <div
          role="status"
          className="border-border/60 bg-muted/40 text-muted-foreground mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          data-testid="sections-readonly-banner"
        >
          <ShieldQuestion
            aria-hidden="true"
            className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
          />
          <span className="flex-1">
            You're viewing this surface in read-only mode. Authoring sections requires the{" "}
            <code className="text-foreground">manage_settings</code> permission.
          </span>
        </div>
      ) : null}

      {noBranchesYet ? (
        <div
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            No branches configured yet. Sections need a branch to belong to — create a branch on
            Settings → Branches first, then come back here to add sections.
          </span>
        </div>
      ) : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn't load sections
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable className="mt-6">
        <DataTableToolbar>
          <div className="flex items-center gap-2">
            <Label htmlFor="sections-branch" className="text-muted-foreground text-xs font-normal">
              Branch
            </Label>
            <Select value={branchSelectValue} onValueChange={handleBranchChange}>
              <SelectTrigger id="sections-branch" className="w-48" disabled={noBranchesYet}>
                <SelectValue placeholder="All branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_BRANCHES_VALUE}>All branches</SelectItem>
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.is_flagship ? `${branch.name} · flagship` : branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(event) => setQInput(event.target.value)}
              placeholder="Filter by grade, name, year, or room…"
              className="pl-8"
              aria-label="Filter sections"
              data-testid="sections-filter"
            />
          </div>
          {qInput ? (
            <Button variant="ghost" size="sm" onClick={() => setQInput("")}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <div className="flex items-center gap-2 pl-2">
            <Checkbox
              id="sections-include-archived"
              checked={includeArchived}
              onCheckedChange={(value) => setIncludeArchived(value === true)}
            />
            <Label
              htmlFor="sections-include-archived"
              className="text-muted-foreground text-xs font-normal"
            >
              Show archived
            </Label>
          </div>
          <DataTableToolbarSpacer />
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[16%]">Branch</TableHead>
                <TableHead className="w-[10%]">Grade</TableHead>
                <TableHead className="w-[12%]">Section</TableHead>
                <TableHead className="w-[16%]">Academic year</TableHead>
                <TableHead className="w-[10%] text-right">Order</TableHead>
                <TableHead className="w-[12%] text-right">Capacity</TableHead>
                <TableHead className="w-[12%]">Status</TableHead>
                <TableHead className="w-[12%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={8} />
            ) : (
              <TableBody>
                {items.map((section) => (
                  <SectionRow
                    key={section.id}
                    section={section}
                    branchName={branchLookup.get(section.branch_id) ?? section.branch_id}
                    canWrite={canWrite}
                    onEdit={openEdit}
                    onArchive={(target) => {
                      setArchiveError(null);
                      setPendingArchive(target);
                    }}
                    onUnarchive={(target) => void handleUnarchive(target)}
                    isUnarchiving={unarchiveMutation.isPending}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<Layers aria-hidden="true" />}
            title={isError ? "Couldn't load sections" : qInput ? "No matches" : "No sections yet"}
            description={
              isError
                ? "Refresh to retry — if it keeps failing, check the network."
                : qInput
                  ? "Try a different filter or clear it to see the full list."
                  : noBranchesYet
                    ? "Create at least one branch first — sections need a branch to belong to."
                    : "Create your first section. The timetable solver and attendance roll-call both fan out across one row per section per slot."
            }
            action={
              isError ? (
                <Button variant="outline" size="sm" onClick={() => void refetch()}>
                  <RefreshCw aria-hidden="true" className="size-4" />
                  Retry
                </Button>
              ) : qInput ? (
                <Button variant="outline" size="sm" onClick={() => setQInput("")}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filter
                </Button>
              ) : canWrite && !noBranchesYet ? (
                <Button size="sm" onClick={openCreate}>
                  <Plus aria-hidden="true" className="size-4" />
                  New section
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && total > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {items.length.toLocaleString()} of {total.toLocaleString()}
              {qInput ? " (filtered)" : ""}
            </span>
          </DataTableFooter>
        ) : null}
      </DataTable>

      <Card className="bg-muted/40 mt-4">
        <CardContent className="text-muted-foreground py-3 text-xs">
          <strong className="text-foreground">How it's used:</strong> sections anchor every
          timetable slot, the attendance roll-call sheet, and a teacher's class-teacher assignment.
          The catalogue readiness aggregator counts active sections per branch + year and reports
          them as the timetable solver's primary input.
        </CardContent>
      </Card>

      <SectionEditorSheet
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) setEditingSection(null);
        }}
        existing={editingSection}
        defaultBranchId={defaultBranchId}
        onSaved={handleSaved}
      />

      <Dialog
        open={Boolean(pendingArchive)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingArchive(null);
            setArchiveError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Archive {pendingArchive?.grade_ref}-{pendingArchive?.name}?
            </DialogTitle>
            <DialogDescription>
              The section is hidden from new timetable runs and class-teacher assignments. Existing
              schedules and roster entries remain intact — restore the section any time to bring it
              back into authoring flows.
            </DialogDescription>
          </DialogHeader>
          {archiveError ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {archiveError}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={archiveMutation.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmArchive()}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Archiving…
                </>
              ) : (
                <>
                  <Trash2 aria-hidden="true" className="size-4" />
                  Archive section
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface SectionRowProps {
  section: Section;
  branchName: string;
  canWrite: boolean;
  onEdit: (section: Section) => void;
  onArchive: (section: Section) => void;
  onUnarchive: (section: Section) => void;
  isUnarchiving: boolean;
}

function SectionRow({
  section,
  branchName,
  canWrite,
  onEdit,
  onArchive,
  onUnarchive,
  isUnarchiving,
}: SectionRowProps) {
  const isArchived = section.archived_at != null;
  const capacityLabel =
    section.max_students == null ? "—" : `${section.current_student_count}/${section.max_students}`;

  return (
    <TableRow data-testid={`sections-row-${section.id}`}>
      <TableCell>
        <span className="text-foreground text-sm">{branchName}</span>
      </TableCell>
      <TableCell>
        <code className="text-foreground text-xs">{section.grade_ref}</code>
      </TableCell>
      <TableCell>
        <span className="text-foreground text-sm font-medium">{section.name}</span>
      </TableCell>
      <TableCell>
        <code className="text-muted-foreground text-xs">{section.academic_year_id}</code>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        <span className="text-foreground text-xs">{section.display_order}</span>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        <span className="text-foreground text-xs">{capacityLabel}</span>
      </TableCell>
      <TableCell>
        <StatusPill status={isArchived ? "archived" : "active"} />
      </TableCell>
      <TableCell className="text-right">
        {canWrite ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${section.grade_ref}-${section.name}`}
              >
                <MoreHorizontal aria-hidden="true" className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onEdit(section)}>
                <Pencil aria-hidden="true" className="size-4" />
                Edit
              </DropdownMenuItem>
              {isArchived ? (
                <DropdownMenuItem onSelect={() => onUnarchive(section)} disabled={isUnarchiving}>
                  <ArchiveRestore aria-hidden="true" className="size-4" />
                  Restore
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={() => onArchive(section)} className="text-destructive">
                  <Trash2 aria-hidden="true" className="size-4" />
                  Archive
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
